/**
 * ColourMatch Pro · Dashboard — action-first landing.
 *
 * Founder direction (2026-02-09):
 *   The first thing a painting business sees is the work they came to do
 *   — not statistics. Actions live at the top. Client engagement (real
 *   events only) sits under actions. Stats + recent lists sit below.
 *
 * Real-data-only rule (locked): every number, name and timestamp on this
 * page comes from a real backend endpoint. Nothing is fabricated.
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Plus,
  ArrowRight,
  FolderKanban,
  Inbox,
  FileText,
  Image as ImageIcon,
  Presentation as PresentationIcon,
  Loader2,
  Eye,
  Send,
  Download,
  CheckCircle2,
  XCircle,
  Rocket,
} from "lucide-react";

export default function ProDashboard() {
  const { me } = useOutletContext();
  const nav = useNavigate();
  const [data, setData] = useState({ loading: true });
  const [engagement, setEngagement] = useState({ loading: true, items: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      const [projRes, leadsRes, quotesRes, rendersRes] = await Promise.all([
        api.get("/pro/cms/projects?page_size=25").then((r) => r.data).catch(() => ({ items: [], total: 0 })),
        api.get("/painter/leads?page_size=100").then((r) => r.data).catch(() => ({ items: [] })),
        api.get("/painter/quotes").then((r) => r.data).catch(() => []),
        api.get("/visualiser/projects").then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      if (!alive) return;
      const projItems = projRes.items || [];
      const renderItems = ((rendersRes.items || rendersRes) || []).filter((r) => !r.is_deleted);
      const quoteItems = Array.isArray(quotesRes) ? quotesRes : (quotesRes.items || []);
      setData({
        loading: false,
        projectsTotal: projRes.total || projItems.length,
        projectsActive: projItems.filter((p) => p.status === "active").length,
        projectsDraft: projItems.filter((p) => p.status === "draft").length,
        recentProjects: [...projItems]
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          .slice(0, 5),
        continueProject: [...projItems]
          .filter((p) => p.status !== "archived")
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))[0] || null,
        leadsNew: (leadsRes.items || []).filter((l) => (l.painter_lead_status || l.status) === "new").length,
        quotesOpen: quoteItems.filter((q) => q.status && q.status !== "accepted" && q.status !== "declined").length,
        rendersTotal: renderItems.length,
        recentRenders: [...renderItems]
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          .slice(0, 3),
      });
    })();
    // Client engagement — real events only. Never fabricated.
    api.get("/pro/cms/engagement/recent?limit=6")
      .then((r) => alive && setEngagement({ loading: false, items: r.data?.items || [] }))
      .catch(() => alive && setEngagement({ loading: false, items: [] }));
    return () => { alive = false; };
  }, []);

  const firstName = (me?.owner_name || "").split(" ")[0]?.trim();
  const continueId = data.continueProject?.id;
  const continueName = data.continueProject?.name;

  return (
    <div className="space-y-10" data-testid="pro-dashboard">
      {/* -------- Greeting -------- */}
      <header>
        <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink leading-tight">
          {firstName ? `Good to see you, ${firstName}.` : "Welcome to ColourMatch Pro."}
        </h1>
        <p className="text-sm text-brand-muted mt-2 max-w-2xl leading-relaxed">
          Prepare client renders, colour selections, visualisation proposals and quotes — all in one professional workspace.
        </p>
      </header>

      {/* -------- ACTIONS (top, action-first) -------- */}
      <section>
        <div className="overline text-brand-muted mb-3">Start work</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pro-action-tiles">
          <ActionTile
            testid="action-new-project"
            title="Start a new client project"
            hint="Create a project with site photos and client details."
            icon={Plus}
            variant="primary"
            onClick={() => nav("/pro/projects?new=1")}
          />
          <ActionTile
            testid="action-continue-project"
            title={continueName ? "Continue an existing project" : "Open your projects"}
            hint={continueName ? continueName : "Pick up where you left off."}
            icon={FolderKanban}
            onClick={() => nav(continueId ? `/pro/projects/${continueId}` : "/pro/projects")}
            disabled={!data.projectsTotal && !data.loading}
          />
          <ActionTile
            testid="action-quick-render"
            title="Open the Colour Workspace"
            hint="Render a property photo with any of the 1,000+ ColourMatch colours."
            icon={ImageIcon}
            onClick={() => nav("/pro/renders")}
          />
          <ActionTile
            testid="action-prepare-quote"
            title="Prepare a quote"
            hint="Build a professional quote for a client."
            icon={FileText}
            onClick={() => nav("/pro/quotes/new")}
          />
          <ActionTile
            testid="action-build-presentation"
            title="Build a proposal"
            hint="Open a project to prepare a visualisation proposal."
            icon={PresentationIcon}
            onClick={() => nav(continueId ? `/pro/projects/${continueId}` : "/pro/projects")}
          />
        </div>
      </section>

      {/* -------- Client Engagement (real events) -------- */}
      <section>
        <SectionHeader
          title="Client engagement"
          hint="Real activity from your proposals"
          linkTo="/pro/presentations"
          linkLabel="Open Proposals"
        />
        <ClientEngagementStrip engagement={engagement} />
      </section>

      {/* -------- Snapshot stats (under actions) -------- */}
      <section>
        <div className="overline text-brand-muted mb-3">Snapshot</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pro-stat-tiles">
          <StatTile
            testid="stat-active-projects"
            label="Active projects"
            value={data.loading ? null : data.projectsActive}
            hint={data.projectsTotal > 0 ? "In progress with clients" : "None yet"}
            icon={FolderKanban}
            to="/pro/projects"
          />
          <StatTile
            testid="stat-draft-projects"
            label="Draft projects"
            value={data.loading ? null : data.projectsDraft}
            hint={data.projectsTotal > 0 ? "Not yet published" : "None yet"}
            icon={FolderKanban}
            to="/pro/projects"
          />
          <StatTile
            testid="stat-new-leads"
            label="New client leads"
            value={data.loading ? null : data.leadsNew}
            hint={data.leadsNew > 0 ? "Respond today to win the work" : "No new enquiries"}
            icon={Inbox}
            to="/pro/leads"
            accent={data.leadsNew > 0}
          />
          <StatTile
            testid="stat-open-quotes"
            label="Open quotes"
            value={data.loading ? null : data.quotesOpen}
            hint={data.quotesOpen > 0 ? "Awaiting client decision" : "None open"}
            icon={FileText}
            to="/pro/quotes"
          />
        </div>
      </section>

      {/* -------- Recent projects -------- */}
      <section>
        <SectionHeader
          title="Recent projects"
          hint={data.projectsTotal ? `${data.projectsTotal} total` : null}
          linkTo="/pro/projects"
          linkLabel="View all"
        />
        {data.loading ? (
          <LoadingCard />
        ) : (data.recentProjects || []).length === 0 ? (
          <EmptyCard
            icon={FolderKanban}
            title="No client projects yet"
            body="Start your first client project to prepare colour renders, presentations and quotes."
            ctaLabel="Create a project"
            ctaTo="/pro/projects?new=1"
            testid="empty-recent-projects"
          />
        ) : (
          <ul className="divide-y divide-brand-linen rounded-2xl border border-brand-linen bg-white overflow-hidden" data-testid="recent-projects-list">
            {data.recentProjects.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/pro/projects/${p.id}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-brand-linen/20 transition-colors"
                  data-testid={`recent-project-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-brand-ink truncate">{p.name}</div>
                    <div className="text-xs text-brand-muted mt-0.5 truncate">
                      {p.client_name || "—"}{p.site_address ? ` · ${p.site_address}` : ""}
                    </div>
                  </div>
                  <StatusChip status={p.status} />
                  <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* -------- Recent renders -------- */}
      <section>
        <SectionHeader
          title="Recent renders"
          hint={data.rendersTotal > 0 ? `${data.rendersTotal} total` : null}
          linkTo="/pro/renders"
          linkLabel="Open Renders"
        />
        {data.loading ? (
          <LoadingCard />
        ) : (data.recentRenders || []).length === 0 ? (
          <EmptyCard
            icon={ImageIcon}
            title="No renders yet"
            body="Add a site photo to produce your first photo-realistic colour render."
            ctaLabel="Start a render"
            ctaTo="/visualiser/new"
            testid="empty-recent-renders"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="recent-renders-grid">
            {data.recentRenders.map((r) => {
              const cover = (r.photos && r.photos[0]?.thumb_url) || (r.photos && r.photos[0]?.url) || null;
              return (
                <Link
                  key={r.id}
                  to={`/visualiser/project/${r.id}`}
                  className="group rounded-2xl border border-brand-linen bg-white overflow-hidden hover:border-brand-teal/40 transition-colors"
                  data-testid={`recent-render-${r.id}`}
                >
                  <div className="aspect-[4/3] bg-brand-linen/40 overflow-hidden">
                    {cover ? (
                      <img src={cover} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-muted">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-medium text-brand-ink truncate">{r.title || "Untitled render"}</div>
                    <div className="text-xs text-brand-muted mt-0.5">
                      {r.render_count || 0} render{(r.render_count || 0) === 1 ? "" : "s"}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action tile — top of dashboard
// ---------------------------------------------------------------------------
function ActionTile({ testid, title, hint, icon: Icon, onClick, variant, disabled }) {
  const base = "text-left rounded-2xl p-5 border transition-all flex flex-col gap-3 h-full";
  const primary = "border-transparent text-white shadow-lg";
  const primaryStyle = variant === "primary" ? { background: "linear-gradient(135deg, #0B1E24 0%, #1F3D47 55%, #2EB3A0 130%)" } : undefined;
  const normal = "border-brand-linen bg-white hover:border-brand-teal/50 hover:shadow-sm";
  const dis = "opacity-50 cursor-not-allowed";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`${base} ${variant === "primary" ? primary : normal} ${disabled ? dis : ""}`}
      style={primaryStyle}
    >
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        variant === "primary" ? "bg-white/20" : "bg-brand-teal/10 text-brand-teal"
      }`}>
        <Icon className={`w-4 h-4 ${variant === "primary" ? "text-white" : ""}`} />
      </span>
      <div>
        <div className={`font-semibold text-sm leading-snug ${variant === "primary" ? "text-white" : "text-brand-ink"}`}>
          {title}
        </div>
        <div className={`text-xs mt-1 leading-relaxed ${variant === "primary" ? "text-white/75" : "text-brand-muted"}`}>
          {hint}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Client engagement strip — real presentation events
// ---------------------------------------------------------------------------
function ClientEngagementStrip({ engagement }) {
  // Locked event labels (2026-02 platform direction) — every row must lead
  // with the client name, then a single, human-readable event title. No
  // sentences, no verbs — the goal is scannability. Copy examples:
  //   • Sarah Miller — Visualisation Proposal Sent
  //   • Sarah Miller — Proposal Viewed
  //   • Sarah Miller — Proposal Accepted
  const eventCopy = useMemo(
    () => (item) => {
      const who = item.client_name || item.project_name || "Client";
      const project = item.project_name || item.presentation_title || "";
      switch (item.event) {
        case "published":
          return { label: "Visualisation Proposal Published", icon: Rocket, tone: "green" };
        case "email_sent":
          return { label: "Visualisation Proposal Sent",      icon: Send,    tone: "blue" };
        case "viewed":
          return { label: "Proposal Viewed",                   icon: Eye,     tone: "teal" };
        case "accepted":
          return { label: "Proposal Accepted",                 icon: CheckCircle2, tone: "green" };
        case "pdf_downloaded":
          return { label: "Proposal PDF Downloaded",           icon: Download, tone: "amber" };
        case "revoked":
          return { label: "Share Link Revoked",                icon: XCircle,  tone: "grey" };
        default:
          return { label: item.event.replace(/_/g, " "),       icon: CheckCircle2, tone: "grey" };
      }
    },
    [],
  );

  if (engagement.loading) {
    return <LoadingCard />;
  }
  if (!engagement.items.length) {
    return (
      <div className="rounded-2xl border border-brand-linen bg-white p-6 text-sm text-brand-muted" data-testid="engagement-empty">
        No client engagement yet. Once you publish a visualisation proposal and your client opens it, activity will appear here.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-brand-linen rounded-2xl border border-brand-linen bg-white overflow-hidden" data-testid="engagement-list">
      {engagement.items.map((item) => {
        const c = eventCopy(item);
        const Icon = c.icon;
        const toneClass = {
          teal:  "bg-brand-teal/10 text-brand-teal",
          blue:  "bg-blue-50 text-blue-700",
          amber: "bg-amber-50 text-amber-800",
          green: "bg-emerald-50 text-emerald-700",
          grey:  "bg-brand-linen/60 text-brand-ink/70",
        }[c.tone];
        const clientName = item.client_name || item.project_name || "Client";
        const secondary = item.project_name && item.client_name && item.project_name !== item.client_name
          ? item.project_name
          : null;
        return (
          <li key={item.id}>
            <Link
              to={`/pro/projects/${item.consultation_id}`}
              className="flex items-center gap-3 px-5 py-3.5 hover:bg-brand-linen/20 transition-colors"
              data-testid={`engagement-item-${item.id}`}
            >
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${toneClass}`}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-brand-ink truncate">
                  <span className="font-semibold">{clientName}</span>
                  <span className="text-brand-muted"> — </span>
                  <span>{c.label}</span>
                </div>
                <div className="text-xs text-brand-muted mt-0.5 flex items-center gap-1.5">
                  <span>{relativeTime(item.created_at)}</span>
                  {secondary && <><span className="text-brand-muted/40">·</span><span className="truncate">{secondary}</span></>}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Small helpers reused from prior version
// ---------------------------------------------------------------------------
function StatTile({ testid, label, value, hint, icon: Icon, to, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group rounded-2xl border p-4 flex flex-col justify-between transition-all ${
        accent ? "border-brand-teal/50 bg-brand-teal/5 hover:bg-brand-teal/10"
        : "border-brand-linen bg-white hover:border-brand-teal/40"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="overline text-brand-muted">{label}</div>
        <Icon className="w-4 h-4 text-brand-muted group-hover:text-brand-teal transition-colors" />
      </div>
      <div className="mt-3">
        {value == null ? (
          <div className="h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-brand-muted" /></div>
        ) : (
          <div className="font-serif text-3xl text-brand-ink leading-none">{value}</div>
        )}
        <div className="text-[11px] text-brand-muted mt-2 truncate">{hint}</div>
      </div>
    </Link>
  );
}

function SectionHeader({ title, hint, linkTo, linkLabel }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-4">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-serif text-xl text-brand-ink">{title}</h2>
        {hint && <span className="text-xs text-brand-muted">{hint}</span>}
      </div>
      {linkTo && (
        <Link to={linkTo} className="text-xs text-brand-teal hover:underline inline-flex items-center gap-1">
          {linkLabel} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    draft:    { bg: "bg-brand-linen/60", fg: "text-brand-ink",    label: "Draft" },
    active:   { bg: "bg-brand-teal/10",  fg: "text-brand-teal",   label: "Active" },
    archived: { bg: "bg-brand-ink/10",   fg: "text-brand-ink/60", label: "Archived" },
  };
  const s = map[status] || map.draft;
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${s.bg} ${s.fg}`}>{s.label}</span>;
}

function LoadingCard() {
  return (
    <div className="border border-brand-linen bg-white rounded-2xl p-6 flex items-center gap-3 text-brand-muted text-sm">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}

function EmptyCard({ icon: Icon, title, body, ctaLabel, ctaTo, testid }) {
  return (
    <div className="border border-dashed border-brand-linen bg-white rounded-2xl p-8 lg:p-10 text-center" data-testid={testid}>
      <div className="w-11 h-11 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-serif text-lg text-brand-ink mb-2">{title}</div>
      <p className="text-sm text-brand-muted max-w-md mx-auto mb-5 leading-relaxed">{body}</p>
      <Link to={ctaTo} className="inline-flex items-center gap-1.5 h-10 px-5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors">
        <Plus className="w-3.5 h-3.5" /> {ctaLabel}
      </Link>
    </div>
  );
}
